import type { PresentationGraphExtension, PresentationWarning } from './contracts';
import type { PresentationGraphInput } from './graph-input';

/**
 * SPEC-011 PRES-REQ-016/§69/§70/plan §36 — pure normalizer: a node/edge may
 * be deleted after a highlight/note references it. This never crashes and
 * never silently drops the note/highlight itself — only the dangling ref is
 * pruned, and a warning is surfaced for the config UI (AC-066-069).
 */
export interface NormalizedPresentationResult {
  config: PresentationGraphExtension;
  warnings: PresentationWarning[];
}

export function normalizePresentationConfig(
  config: PresentationGraphExtension,
  graph: PresentationGraphInput,
): NormalizedPresentationResult {
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  const edgeIds = new Set(graph.edges.map((edge) => edge.id));
  const warnings: PresentationWarning[] = [];

  const strategicNotes = config.strategicNotes.map((note) => {
    const validNodeIds = note.nodeIds.filter((id) => nodeIds.has(id));
    if (validNodeIds.length < note.nodeIds.length) {
      warnings.push({
        code: 'note_missing_node_refs',
        message: `Strategic note "${note.title}" references a node that no longer exists.`,
        entityId: note.id,
      });
    }
    return { ...note, nodeIds: validNodeIds };
  });

  const highlights = config.highlights.map((highlight) => {
    const validNodeIds = highlight.nodeIds.filter((id) => nodeIds.has(id));
    const validEdgeIds = highlight.edgeIds.filter((id) => edgeIds.has(id));
    if (
      validNodeIds.length < highlight.nodeIds.length ||
      validEdgeIds.length < highlight.edgeIds.length
    ) {
      warnings.push({
        code: 'highlight_missing_refs',
        message: `Highlight "${highlight.label}" references a node or edge that no longer exists.`,
        entityId: highlight.id,
      });
    }
    if (validNodeIds.length === 0 && validEdgeIds.length === 0) {
      warnings.push({
        code: 'highlight_empty',
        message: `Highlight "${highlight.label}" has no valid references left and will not appear in the presentation.`,
        entityId: highlight.id,
      });
    }
    return { ...highlight, nodeIds: validNodeIds, edgeIds: validEdgeIds };
  });

  return { config: { ...config, strategicNotes, highlights }, warnings };
}

/** §70 — a highlight with no valid refs is excluded from the active presentation flow (still visible in settings via the warning above). */
export function renderableHighlights(config: PresentationGraphExtension): typeof config.highlights {
  return config.highlights.filter(
    (highlight) => highlight.nodeIds.length > 0 || highlight.edgeIds.length > 0,
  );
}
